import React, { useState, useEffect } from 'react'
import { 
  BarChart2, Users, FileText, CheckCircle, 
  Download, FileOutput, Search 
} from 'lucide-react'
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar,
  PieChart, Pie, Cell, Legend
} from 'recharts'
import axiosInstance from '../../services/axiosInstance'
import { useAuth } from '../../contexts/AuthContext'
import { isAdmin } from '../../utils/roleUtils'
import PageHeader from '../../components/common/PageHeader'
import './styles.scss'

export default function Analytics() {
  const { user } = useAuth()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [programs, setPrograms] = useState([])

  const userIsAdmin = isAdmin(user)
  const [statusFilter, setStatusFilter] = useState('ALL')
  const [programIdFilter, setProgramIdFilter] = useState(userIsAdmin ? 'ALL' : (user?.managedProgramIds?.[0] || ''))
  const [startDateFilter, setStartDateFilter] = useState('')
  const [endDateFilter, setEndDateFilter] = useState('')

  const fetchAnalytics = async () => {
    try {
      setLoading(true)
      const params = new URLSearchParams()
      if (statusFilter !== 'ALL') params.append('status', statusFilter)
      if (programIdFilter !== 'ALL') params.append('programId', programIdFilter)
      if (startDateFilter) params.append('startDate', startDateFilter)
      if (endDateFilter) params.append('endDate', endDateFilter)

      const response = await axiosInstance.get(`/api/analytics/dashboard?${params.toString()}`)
      if (response.data.success) {
        setData(response.data.data)
      } else {
        setError(response.data.message)
      }
    } catch (err) {
      setError('Failed to load analytics data')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAnalytics()
  }, [])

  useEffect(() => {
    // Fetch available programs for the dropdown
    axiosInstance.get('/api/settings/programs')
      .then(res => {
        let fetchedPrograms = Array.isArray(res.data) ? res.data : (res.data?.data || []);
        if (!userIsAdmin && user?.managedProgramIds) {
          fetchedPrograms = fetchedPrograms.filter(p => user.managedProgramIds.includes(p.id));
          if (fetchedPrograms.length > 0 && !user.managedProgramIds.includes(programIdFilter)) {
            setProgramIdFilter(fetchedPrograms[0].id);
          }
        }
        setPrograms(fetchedPrograms)
      })
      .catch(console.error)
  }, [userIsAdmin, user])

  const [exporting, setExporting] = useState(false)

  const exportToPDF = async () => {
    try {
      setExporting(true)
      const params = new URLSearchParams()
      if (statusFilter !== 'ALL') params.append('status', statusFilter)
      if (programIdFilter !== 'ALL') params.append('programId', programIdFilter)
      if (startDateFilter) params.append('startDate', startDateFilter)
      if (endDateFilter) params.append('endDate', endDateFilter)

      const response = await axiosInstance.get(`/api/analytics/export/pdf?${params.toString()}`, { responseType: 'blob' })
      const blob = new Blob([response.data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'analytics_report.pdf')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (err) {
      console.error("Failed to export PDF", err)
      alert("Failed to generate PDF. Please try again.")
    } finally {
      setExporting(false)
    }
  }

  const exportToCSV = async () => {
    try {
      setExporting(true)
      const params = new URLSearchParams()
      if (statusFilter !== 'ALL') params.append('status', statusFilter)
      if (programIdFilter !== 'ALL') params.append('programId', programIdFilter)
      if (startDateFilter) params.append('startDate', startDateFilter)
      if (endDateFilter) params.append('endDate', endDateFilter)

      const response = await axiosInstance.get(`/api/analytics/export/csv?${params.toString()}`, { responseType: 'blob' })
      const blob = new Blob([response.data], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'analytics_report.csv')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (err) {
      console.error("Failed to export CSV", err)
      alert("Failed to generate CSV. Please try again.")
    } finally {
      setExporting(false)
    }
  }

  if (loading) return <div className="p-8">Loading analytics...</div>
  if (error) return <div className="p-8 text-red-500">{error}</div>
  if (!data) return null

  return (
    <div className="analytics-dashboard">
      <PageHeader 
        title="Reports & Analytics" 
        subtitle="Gain insights into your admissions pipeline and applicant demographics."
      />

      <div className="filter-bar glass-panel">
        <div className="filter-group">
          <label>Program</label>
          <select 
            value={programIdFilter} 
            onChange={e => setProgramIdFilter(e.target.value)}
          >
            {userIsAdmin && <option value="ALL">All Programs</option>}
            {programs.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
        
        <div className="filter-group">
          <label>Status</label>
          <select 
            value={statusFilter} 
            onChange={e => setStatusFilter(e.target.value)}
          >
            <option value="ALL">All Statuses</option>
            <option value="APPLICATION_STARTED">Started</option>
            <option value="SUBMITTED">Submitted</option>
            <option value="UNDER_REVIEW">Under Review</option>
            <option value="ADMITTED">Admitted</option>
            <option value="REJECTED">Rejected</option>
            <option value="INFO_REQUESTED">Info Requested</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Start Date</label>
          <input 
            type="date" 
            value={startDateFilter}
            onChange={e => setStartDateFilter(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label>End Date</label>
          <input 
            type="date" 
            value={endDateFilter}
            onChange={e => setEndDateFilter(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label>&nbsp;</label>
          <button className="btn btn-primary" onClick={fetchAnalytics} disabled={loading} style={{ height: '35px' }}>
            <Search size={16} /> Search
          </button>
        </div>

        <div className="filter-spacer"></div>

        <div className="export-actions">
          <button className="btn-export btn-csv" onClick={exportToCSV} disabled={exporting}>
            <Download size={16} /> {exporting ? 'Generating...' : 'Export CSV'}
          </button>
          <button className="btn-export btn-pdf" onClick={exportToPDF} disabled={exporting}>
            <FileOutput size={16} /> {exporting ? 'Generating...' : 'Export PDF'}
          </button>
        </div>
      </div>

      <div className="kpi-grid">
        <div className="glass-panel kpi-card">
          <div className="kpi-icon blue"><FileText size={24} /></div>
          <div className="kpi-content">
            <h3>Total Applications</h3>
            <p className="kpi-value">{data.totalApplications}</p>
          </div>
        </div>
        <div className="glass-panel kpi-card">
          <div className="kpi-icon yellow"><BarChart2 size={24} /></div>
          <div className="kpi-content">
            <h3>Under Review</h3>
            <p className="kpi-value">{data.underReview}</p>
          </div>
        </div>
        <div className="glass-panel kpi-card">
          <div className="kpi-icon green"><CheckCircle size={24} /></div>
          <div className="kpi-content">
            <h3>Admitted</h3>
            <p className="kpi-value">{data.admittedStudents}</p>
          </div>
        </div>
        <div className="glass-panel kpi-card">
          <div className="kpi-icon purple"><Users size={24} /></div>
          <div className="kpi-content">
            <h3>Approval Rate</h3>
            <p className="kpi-value">{data.approvalRate}%</p>
          </div>
        </div>
        <div className="glass-panel kpi-card">
          <div className="kpi-icon light-blue"><FileText size={24} /></div>
          <div className="kpi-content">
            <h3>I-20 Issuance Rate</h3>
            <p className="kpi-value">{data.i20IssuanceRate}%</p>
          </div>
        </div>
        <div className="glass-panel kpi-card">
          <div className="kpi-icon pink"><CheckCircle size={24} /></div>
          <div className="kpi-content">
            <h3>Visa Success Rate</h3>
            <p className="kpi-value">{data.visaSuccessRate}%</p>
          </div>
        </div>
      </div>

      <div className="charts-grid">
        <div className="glass-panel chart-card">
          <h3>Application Volume (Last 30 Days)</h3>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.applicationsOverTime}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{fontSize: 12, fill: '#64748b'}} tickLine={false} axisLine={false} />
                <YAxis tick={{fontSize: 12, fill: '#64748b'}} tickLine={false} axisLine={false} />
                <Tooltip 
                  contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                />
                <Line type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={3} dot={{r: 4, fill: '#3b82f6', strokeWidth: 2, stroke: '#fff'}} activeDot={{r: 6}} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="glass-panel chart-card">
          <h3>Application Status</h3>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data.applicationsByStatus}
                  cx="50%"
                  cy="45%"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {data.applicationsByStatus.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }} />
                <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ fontSize: '12px' }}/>
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="glass-panel chart-card demographics-card">
        <h3>Top Applicant Demographics</h3>
        <div className="chart-container">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.topCountries} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
              <XAxis dataKey="country" tick={{fontSize: 12, fill: '#64748b'}} tickLine={false} axisLine={false} />
              <YAxis tick={{fontSize: 12, fill: '#64748b'}} tickLine={false} axisLine={false} />
              <Tooltip cursor={{fill: '#f8fafc'}} contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }} />
              <Bar dataKey="applicants" fill="#a855f7" radius={[4, 4, 0, 0]} barSize={32} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="glass-panel chart-card detailed-list-card">
        <h3>Detailed Application List</h3>
        <p className="card-subtitle">Showing recent applications matching your filters. Export to CSV for the full list.</p>
        
        <div className="table-responsive">
          <table className="data-table">
            <thead>
              <tr>
                <th>App Number</th>
                <th>Applicant Name</th>
                <th>Program</th>
                <th>Status</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {data.applicationsList && data.applicationsList.slice(0, 50).map(app => (
                <tr key={app.id}>
                  <td className="td-app-number">{app.applicationNumber}</td>
                  <td className="td-app-name">{app.applicantName}</td>
                  <td className="td-app-program">{app.programName}</td>
                  <td>
                    <span className={`status-badge ${app.status === 'ADMITTED' ? 'admitted' : 'default'}`}>
                      {app.status?.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="td-app-date">{app.submissionDate}</td>
                </tr>
              ))}
              {(!data.applicationsList || data.applicationsList.length === 0) && (
                <tr>
                  <td colSpan={5} className="td-empty">
                    No applications found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
